import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { HStack, Box, Text, Button, Input } from '@chakra-ui/react';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { Formik } from 'formik';
import { fetchWrapper } from '../../helpers';
import API from '../../helpers/axiosInstance';

// Variable names already covered by their own dedicated inputs below —
// never re-render these as generic dynamic fields even if the flow's
// variable list happens to include them.
const RESERVED_VARIABLE_NAMES = new Set(['phone', 'email', 'flow_id']);

const normalizeVariable = (entry) => {
  if (typeof entry === 'string') return { name: entry, example: '' };
  return {
    name: entry?.fieldName || entry?.name || entry?.key || entry?.field || '',
    example: entry?.example || '',
  };
};

const FlowApi = () => {
  const [flowOptions, setFlowOptions] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(false);

  // Kept in memory only — never localStorage, so the key isn't readable by any
  // other script/tab or left behind after the page closes.
  const [apiKey, setApiKey] = useState('');

  const FormSchema = Yup.object().shape({
    flow_id: Yup.string().required('Flow is required'),
    to: Yup.string()
      .matches(/^[0-9+\-\s()]+$/, 'Phone number can only contain numbers and special characters')
      .required('Phone number is required'),
  });

  const FlowList = async () => {
    try {
      const url = `${process.env.REACT_APP_FLOW_API_URL}/flow/api-list`;
      const data = await fetchWrapper.get(url);
      const rawFlows = Array.isArray(data) ? data : (data.data || data.flows || []);
      const activeFlows = rawFlows.filter((flow) => flow.status === 'active');

      const transformed = activeFlows.map((flow) => {
        const rawVariables = flow.trigger_contract_fields || flow.variables || flow.variable_list || flow.triggerContractFields || flow.fields || flow.api_variables || [];
        const variableFields = rawVariables
          .map(normalizeVariable)
          .filter((v) => v.name && !RESERVED_VARIABLE_NAMES.has(v.name.toLowerCase()));

        return {
          label: flow.name || flow.flow_name || flow.title,
          value: flow._id || flow.id || flow.flow_id,
          variableFields,
        };
      });

      setFlowOptions(transformed);
    } catch (error) {
      console.error('Error fetching flows:', error);
      toast('Error fetching flows', { type: 'error' });
    }
  };

  const APIKEYGET = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer/get-key?key_type=api_key`;
      const data = await fetchWrapper.get(url);
      setApiKey(data?.key || '');
    } catch (error) {
      console.error('Error fetching API key:', error);
    }
  };

  useEffect(() => {
    FlowList();
    APIKEYGET();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFlowChange = (option) => {
    setSelectedFlow(option);
    setVariables((option?.variableFields || []).map((v) => ({ key: v.name, example: v.example, value: '' })));
  };

  const handleVariableChange = (index, value) => {
    setVariables((prev) => prev.map((variable, i) => (i === index ? { ...variable, value } : variable)));
  };

  const onSubmit = async () => {
    const variablesObject = {};
    variables.forEach((variable) => {
      if (variable.key && variable.value) {
        variablesObject[variable.key] = variable.value;
      }
    });

    const payload = {
      flow_id: selectedFlow?.value,
      phone: phoneNumber,
      ...(email ? { email } : {}),
      ...variablesObject,
    };

    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_FLOW_API_URL}/flow/trigger`;
      await API.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
      });

      toast('Flow has been triggered', { type: 'info' });
    } catch (error) {
      console.error('Error:', error);
      toast(error?.response?.data?.message || 'Error triggering flow', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sms_body_section">
      <HStack justifyContent="space-between">
        <Box width="100%">
          <Formik
            enableReinitialize
            validationSchema={FormSchema}
            initialValues={{ flow_id: selectedFlow?.value || '', to: phoneNumber }}
            onSubmit={onSubmit}
          >
            {({ errors, touched, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                {/* Flow Select */}
                <Box mb="10px">
                  <Text fontSize="16px" fontWeight="400" mb="5px">
                    Flow <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Select
                    options={flowOptions}
                    value={selectedFlow}
                    onChange={handleFlowChange}
                    placeholder="Select Flow"
                    className={errors.flow_id && touched.flow_id ? 'is-invalid' : ''}
                  />
                  {errors.flow_id && touched.flow_id && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.flow_id}
                    </Text>
                  )}
                </Box>

                {/* Phone Number */}
                <Box mb="10px" mt="10px">
                  <Text fontSize="16px" fontWeight={400} mb="5px">
                    Mobile Number <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter Phone Number starting with 92 or country code"
                  />
                  {errors.to && touched.to && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.to}
                    </Text>
                  )}
                </Box>

                {/* Email */}
                <Box mb="10px" mt="10px">
                  <Text fontSize="16px" fontWeight="400" mb="5px">
                    E-Mail (Optional)
                  </Text>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Please Enter Email ID"
                    type="email"
                    bg="white"
                  />
                </Box>

                {/* Dynamic Flow Variables */}
                {variables.length > 0 && (
                  <>
                    <Text fontSize="16px" fontWeight={400}>
                      Variables
                    </Text>
                    {variables.map((variable, index) => (
                      <HStack key={variable.key} w="100%" mt="5px" alignItems="baseline">
                        <Box w="50%">
                          <Input value={variable.key} isReadOnly placeholder="Variable" bg="gray.100" />
                        </Box>
                        <Box w="50%">
                          <Input
                            value={variable.value}
                            onChange={(e) => handleVariableChange(index, e.target.value)}
                            placeholder={variable.example ? `e.g., ${variable.example}` : 'Value'}
                          />
                        </Box>
                      </HStack>
                    ))}
                  </>
                )}

                {/* Submit */}
                <Button
                  isLoading={loading}
                  py="10px"
                  px="25px"
                  color="white"
                  background={'var(--brand-primary)'}
                  fontSize="14px"
                  fontWeight="500"
                  fontFamily="'Lato', sans-serif"
                  mb={4}
                  mt={4}
                  style={{ width: '100%' }}
                  type="submit"
                >
                  Send Flow Trigger
                </Button>
              </form>
            )}
          </Formik>
        </Box>
      </HStack>
    </div>
  );
};

export default FlowApi;
